import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getEventNameForDraftTransfer, getRecipientTypeParam } from 'apps/auth/components/utils/AmplitudeUtils';
import { RESUME_TRANSFER_CLICKED, VERIFY_IDENTITY_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { FROM, PAYOUT_COUNTRY, RECIPIENT_TYPE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { TRANSFER } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { CATEGORY_TRANSFER_FROM_INTERAC, CATEGORY_TRANSFER_FROM_PEOPLE_TRUST_INTERAC, STATUS_OK } from 'apps/auth/constants/Constants';
import { AUTH_CANCEL_TRANSACTION, AUTH_PAYMENTS_HISTORY } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { getDraftIconForButton, isShowHowToPay } from 'apps/auth/helpers/historyUtils';
import { useCheck, useCheckMeta, useChequeExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import AntifraudRecipientNotifyModal from '../modals/AntifraudRecipientNotifyModal';
import CanceledOperationModal from '../modals/CanceledOperationModal';
import CancelOperationPopup from '../popups/CancelOperationPopup';
import PayOrCanselPopup from '../popups/PayOrCanselPopup';

const MainButton = (props) => {
    const { payOrCanselButton, payCancelButton, payFromInteracButton, draftChequeButton, payBankTransferButton } = usePageMessages();
    const cheque = useCheck();
    const chequeExtra = cheque.extra;
    const checkMeta = useCheckMeta();
    const { i_paid } = useChequeExtra();
    const usaCanDeclinedTill = useMemo(() => cheque?.canDeclinedTill && new Date(cheque.canDeclinedTill) > new Date(), [cheque.canDeclinedTill]);

    const isIPaid = i_paid === '1';

    const { canCancelTransaction } = checkMeta;
    const showHowToPay = isShowHowToPay(cheque);

    const sendCancelTransferAmplitudeEvent = () => {
        const eventParam = { [PAYOUT_COUNTRY]: cheque?.toCountry?.nameEn };
        amplitude.getInstance().logEvent('Cancel Transfers Button Clicked', eventParam);
    };

    const sendCancelTransferPopupShowAmplitudeEvent = () => {
        amplitude.getInstance().logEvent('Cancellation Screen Shown');
    };

    const sendCancelTransferButtonAmplitudeEvent = () => {
        amplitude.getInstance().logEvent('Cancel and Refund Selected');
    };

    const sendAmplitudeEvent = () => {
        const eventName = getEventNameForDraftTransfer(chequeExtra);
        const getEventParams = () => {
            if (eventName === VERIFY_IDENTITY_CLICKED) {
                return { [FROM]: TRANSFER };
            } else if (eventName === RESUME_TRANSFER_CLICKED && cheque.recipientType) {
                return { [RECIPIENT_TYPE]: getRecipientTypeParam(cheque.recipientType) };
            } else {
                return null;
            }
        };
        amplitude.getInstance().logEvent(eventName, getEventParams());
    };

    const cancelTransaction = () => {
        if (canCancelTransaction) {
            props.popupRef.current?.close();
            props.toggleSpinner(true);
            apiFetchData(AUTH_CANCEL_TRANSACTION + '?paymentId=' + cheque.paymentId)
                .then((json) => {
                    if (json.messageBodyMessages) {
                        props.updateMessages(json.messageBodyMessages);
                    }
                    props.updatePageMessages(json.messages).then(() => {
                        if (json.extra.transactionCanceled) {
                            props.modalRef.current.open(<CanceledOperationModal />);
                        }
                    });
                })
                .finally(() => props.toggleSpinner(false));
            sendCancelTransferButtonAmplitudeEvent();
        }
    };

    if (cheque.draft) {
        if (chequeExtra.draftOnfidoGtInProgress || !draftChequeButton) {
            return null;
        }
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="link"
                    isHoverable
                    negativeMargin
                    id={`pay_repeat_link_${cheque.paymentId}`}
                    href={
                        chequeExtra.draftInProgressShowSupportButton || chequeExtra.draftCompletedVerification || cheque.draftFraud
                            ? chequeExtra.continueButtonLink
                            : chequeExtra.kycLink
                    }
                    onClick={(e) => {
                        sendAmplitudeEvent();
                        if (chequeExtra.draftInProgressShowSupportButton || chequeExtra.draftOnfidoTooManyAttempts || chequeExtra.fraudNeedChat) {
                            e.preventDefault();
                            props.toggleChat(true);
                        } else if (chequeExtra.fraudChequeRecipientDetails) {
                            e.preventDefault();
                            props.modalRef.current.open(<AntifraudRecipientNotifyModal extra={cheque.extra} shareDetails={cheque.shareDetails} />);
                        }
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName={getDraftIconForButton(chequeExtra)} />
                    </DiskBadgeWrapper>
                    <Text color="primary">{draftChequeButton}</Text>
                </Panel>
            </>
        );
    } else if (canCancelTransaction && showHowToPay && !usaCanDeclinedTill) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="link"
                    isHoverable
                    negativeMargin
                    id={`pay_cancel_link_${cheque.paymentId}`}
                    href={AUTH_CANCEL_TRANSACTION}
                    onClick={(e) => {
                        sendCancelTransferAmplitudeEvent();
                        sendCancelTransferPopupShowAmplitudeEvent();
                        e.preventDefault();
                        props.popupRef.current?.open(<PayOrCanselPopup popupRef={props.popupRef} cancelAction={cancelTransaction} />);
                    }}
                >
                    <DiskBadgeWrapper>
                        {isIPaid ? <DiskBadge variant="secondary" iconName="cross" /> : <DiskBadge variant="primary" iconName="info-rounded" />}
                    </DiskBadgeWrapper>
                    <Text color="primary">{isIPaid ? payCancelButton : payOrCanselButton}</Text>
                </Panel>
            </>
        );
    } else if (canCancelTransaction && usaCanDeclinedTill) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="link"
                    isHoverable
                    negativeMargin
                    id={`pay_cancel_link_${cheque.paymentId}`}
                    href={AUTH_CANCEL_TRANSACTION}
                    onClick={(e) => {
                        sendCancelTransferAmplitudeEvent();
                        sendCancelTransferPopupShowAmplitudeEvent();
                        e.preventDefault();
                        props.popupRef.current?.open(<CancelOperationPopup popupRef={props.popupRef} cancelAction={cancelTransaction} />);
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="secondary" iconName="cross" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{payCancelButton}</Text>
                </Panel>
            </>
        );
    } else if (cheque.status < STATUS_OK && cheque.providerFromCategory === CATEGORY_TRANSFER_FROM_INTERAC) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel type="link" isHoverable negativeMargin href={`/auth/send/from-interac-info/${cheque.paymentId}`}>
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="info-rounded" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{payFromInteracButton}</Text>
                </Panel>
            </>
        );
    } else if (cheque.status < STATUS_OK && cheque.providerFromCategory === CATEGORY_TRANSFER_FROM_PEOPLE_TRUST_INTERAC) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel type="link" isHoverable negativeMargin href={`/auth/send/from-people-trust-interac-info/${cheque.paymentId}`}>
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="info-rounded" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{payFromInteracButton}</Text>
                </Panel>
            </>
        );
    } else if (showHowToPay) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel type="reactLink" isHoverable negativeMargin to={`${AUTH_PAYMENTS_HISTORY}?paymentId=${cheque.paymentId}`}>
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="info-rounded" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{payBankTransferButton}</Text>
                </Panel>
            </>
        );
    }
};
MainButton.propTypes = {
    toggleSpinner: PropTypes.func.isRequired,
    updateMessages: PropTypes.func.isRequired,
    toggleChat: PropTypes.func.isRequired,
    updatePageMessages: PropTypes.func.isRequired,
    popupRef: PropTypes.object.isRequired,
    modalRef: PropTypes.object.isRequired,
};

export default MainButton;
